import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle, XCircle, Gavel } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useApi, errText } from "@/lib/api";
import { useMe } from "@/components/app/TrialBanner";

const LOOK = {
  approved: { text: "Approved", cls: "border-[#00FF9D]/40 bg-[#00FF9D]/10 text-[#00FF9D]", Icon: CheckCircle },
  rejected: { text: "Rejected", cls: "border-[#FF5C7A]/40 bg-[#FF5C7A]/10 text-[#FF5C7A]", Icon: XCircle },
};

const Badge = ({ id, status }) => {
  const l = LOOK[status];
  if (!l) return null;
  return (
    <span data-testid={`scenario-decision-badge-${id}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${l.cls}`}>
      <l.Icon size={11} weight="fill" /> {l.text}
    </span>
  );
};

export const ScenarioDecision = ({ scenario }) => {
  const api = useApi();
  const qc = useQueryClient();
  const isOwner = useMe().data?.role === "owner";
  const m = useMutation({
    mutationFn: (status) => api.put(`/scenarios/${scenario.id}/decision`, { status }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["scenarios"] }); toast.success(r.decision ? `Marked ${LOOK[r.decision].text}` : "Decision cleared"); },
    onError: (e) => toast.error(errText(e)),
  });
  if (!isOwner) return <Badge id={scenario.id} status={scenario.decision} />;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`scenario-decision-btn-${scenario.id}`} disabled={m.isPending} title="Approve or reject this scenario"
          className="shrink-0 rounded-full outline-none focus-visible:ring-1 focus-visible:ring-[#00FF9D] transition-opacity disabled:opacity-50">
          {scenario.decision ? <Badge id={scenario.id} status={scenario.decision} />
            : <span className="grid h-6 w-6 place-items-center rounded-full text-[#6B6B6B] hover:text-white transition-colors"><Gavel size={14} /></span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[70] border-[#2A2A2A] bg-[#121212] text-white">
        <DropdownMenuItem data-testid={`scenario-approve-${scenario.id}`} onSelect={() => m.mutate("approved")} className="gap-2 text-xs"><CheckCircle className="text-[#00FF9D]" /> Approve</DropdownMenuItem>
        <DropdownMenuItem data-testid={`scenario-reject-${scenario.id}`} onSelect={() => m.mutate("rejected")} className="gap-2 text-xs"><XCircle className="text-[#FF5C7A]" /> Reject</DropdownMenuItem>
        {scenario.decision && <DropdownMenuItem data-testid={`scenario-decision-clear-${scenario.id}`} onSelect={() => m.mutate(null)} className="text-xs text-[#A3A3A3]">Clear decision</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
